import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/datetime";
import { ScrollText, Search, Loader2, ChevronLeft, ChevronRight, X } from "lucide-react";

const PAGE_SIZE = 50;

function ActionBadge({ action }) {
  const a = action || "";
  const tone = a.includes("delete") || a.includes("suspend")
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : a.includes("create") || a.includes("add")
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : a.includes("rebook")
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${tone}`}>
      {a || "—"}
    </span>
  );
}

export default function ActivityLog() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ entries: [], total: 0, total_pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/admin/audit", { params: { q: debouncedQ || undefined, page, page_size: PAGE_SIZE } })
      .then((r) => active && setData(r.data))
      .catch(() => active && setData({ entries: [], total: 0, total_pages: 1, page: 1 }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [debouncedQ, page]);

  const start = data.total === 0 ? 0 : (data.page - 1) * PAGE_SIZE + 1;
  const end = Math.min(data.page * PAGE_SIZE, data.total);

  return (
    <div className="p-6 lg:p-8" data-testid="page-activity-log">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <ScrollText className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Activity Log</h1>
          <p className="text-sm text-slate-500 mt-0.5">Append-only record of changes across your company. Cannot be edited or deleted.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="text-xs text-slate-500">
            {loading
              ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</span>
              : <>Showing <span className="font-semibold text-slate-700">{start.toLocaleString()}</span>–<span className="font-semibold text-slate-700">{end.toLocaleString()}</span> of <span className="font-semibold text-slate-700">{data.total.toLocaleString()}</span></>}
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search action, detail, user, reference…"
              data-testid="activity-log-search"
              className="pl-9 pr-8 h-9 bg-slate-50 border-slate-200"
            />
            {q && (
              <button onClick={() => setQ("")} data-testid="activity-log-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="activity-log-table">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3.5 whitespace-nowrap">When</th>
                <th className="px-6 py-3.5 whitespace-nowrap">Action</th>
                <th className="px-6 py-3.5">Detail</th>
                <th className="px-6 py-3.5 whitespace-nowrap">User</th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.entries.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-16 text-center text-slate-400 text-sm">
                  No activity found{debouncedQ ? ` for “${debouncedQ}”` : ""}.
                </td></tr>
              )}
              {data.entries.map((e, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors" data-testid={`activity-log-row-${i}`}>
                  <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap tabular-nums">{formatDateTime(e.ts)}</td>
                  <td className="px-6 py-4"><ActionBadge action={e.action} /></td>
                  <td className="px-6 py-4 text-sm text-slate-700 max-w-[520px]">
                    {e.detail || <span className="text-slate-300">—</span>}
                    {e.ref_key ? <span className="ml-2 text-xs text-slate-400 mono">({e.ref_key})</span> : null}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">{e.actor_email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">Page {data.page} of {data.total_pages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1} data-testid="activity-log-page-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={data.page >= data.total_pages} data-testid="activity-log-page-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
